import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../App';
import { api, useQuery } from '../api/client';
import type { CallRow } from '../api/types';
import { FilterChip, Select } from '../components/Select';
import { BandPill, BantStrip, Card, Empty, ErrorNote, IconSearch, Score, Skeleton } from '../components/ui';
import { formatDate, formatDuration } from '../lib/format';

const BANDS = ['Excellent', 'Good', 'Needs Work', 'Poor', 'FAIL'];

/** Relative presets, resolved to a date at render so they never go stale. */
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toISOString().slice(0, 10);
const RANGES = [
  { value: '', label: 'Any date' },
  { value: daysAgo(7), label: 'Last 7 days' },
  { value: daysAgo(30), label: 'Last 30 days' },
  { value: daysAgo(90), label: 'Last 90 days' },
];
const STATUSES = ['Booked', 'Pending', 'Done', 'Follow Up', 'Out of Budget', 'Disqualified', 'No Show', 'Rescheduled'];

/** Debounce so typing in search does not fire a request per keystroke. */
function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function CallsPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const debouncedQ = useDebounced(q);

  const [sdrId, setSdrId] = useState(searchParams.get('sdrId') ?? '');
  const [band, setBand] = useState(searchParams.get('band') ?? '');
  const [status, setStatus] = useState(searchParams.get('status') ?? '');
  const [sdrLogStatus, setSdrLogStatus] = useState(searchParams.get('sdrLogStatus') ?? '');
  const [flagged, setFlagged] = useState(searchParams.get('flagged') === 'true');
  const [autoFail, setAutoFail] = useState(searchParams.get('autoFail') === 'true');
  const [mismatch, setMismatch] = useState(searchParams.get('mismatch') === 'true');
  const [from, setFrom] = useState(searchParams.get('from') ?? '');
  const [sort, setSort] = useState<'date' | 'score' | 'sdr'>((searchParams.get('sort') as 'date') ?? 'date');
  const [dir, setDir] = useState<'asc' | 'desc'>((searchParams.get('dir') as 'asc') ?? 'desc');
  const [offset, setOffset] = useState(0);

  const isSdr = user?.role === 'sdr';
  const { data: sdrList } = useQuery(() => api.sdrs(), [], !isSdr);

  const params = useMemo(() => {
    const p: Record<string, string> = { sort, dir, limit: '50', offset: String(offset) };
    if (debouncedQ) p.q = debouncedQ;
    if (sdrId) p.sdrId = sdrId;
    if (band) p.band = band;
    if (status) p.status = status;
    if (sdrLogStatus) p.sdrLogStatus = sdrLogStatus;
    if (flagged) p.flagged = 'true';
    if (autoFail) p.autoFail = 'true';
    if (mismatch) p.mismatch = 'true';
    if (from) p.from = new Date(from).toISOString();
    return p;
  }, [debouncedQ, sdrId, band, status, sdrLogStatus, flagged, autoFail, mismatch, from, sort, dir, offset]);

  useEffect(() => {
    const next = { ...params };
    delete next.limit;
    delete next.offset;
    setSearchParams(next, { replace: true });
  }, [params, setSearchParams]);

  const { data, error, loading, reload } = useQuery(() => api.calls(params), [JSON.stringify(params)]);

  const toggleSort = (key: 'date' | 'score' | 'sdr') => {
    if (sort === key) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(key);
      setDir('desc');
    }
    setOffset(0);
  };

  const arrow = (key: string) => (sort === key ? <span className="sort-arrow">{dir === 'asc' ? '↑' : '↓'}</span> : null);

  const activeFilters =
    [sdrId, band, status, sdrLogStatus, from].filter(Boolean).length + [flagged, autoFail, mismatch].filter(Boolean).length;

  const clearAll = () => {
    setQ('');
    setSdrId('');
    setBand('');
    setStatus('');
    setSdrLogStatus('');
    setFlagged(false);
    setAutoFail(false);
    setMismatch(false);
    setFrom('');
    setOffset(0);
  };

  return (
    <>
      <header className="page-head">
        <div className="page-head-text">
          <h1>{isSdr ? 'My calls' : 'Calls'}</h1>
          <p className="page-sub">
            {data ? `${data.total} call${data.total === 1 ? '' : 's'}` : 'Loading…'}
            {activeFilters > 0 && ` · ${activeFilters} filter${activeFilters === 1 ? '' : 's'} applied`}
          </p>
        </div>
        {!isSdr && (
          <div className="page-head-actions">
            <Link to="/calls/new" className="btn btn-primary">
              Submit a call
            </Link>
          </div>
        )}
      </header>

      <div className="page-body page-enter">
        <div className="toolbar">
          <div className="search">
            <label className="sr-only" htmlFor="search">
              Search prospect, company or SDR
            </label>
            <span className="search-icon" aria-hidden>
              <IconSearch size={14} />
            </span>
            <input
              id="search"
              type="search"
              placeholder="Search prospect, company or SDR…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setOffset(0);
              }}
            />
          </div>

          {!isSdr && (
            <Select
              id="f-sdr"
              ariaLabel="Filter by SDR"
              value={sdrId}
              onChange={(v) => { setSdrId(v); setOffset(0); }}
              options={[{ value: '', label: 'All SDRs' }, ...(sdrList?.sdrs ?? []).map((s) => ({ value: s.id, label: s.name }))]}
            />
          )}

          <Select
            id="f-band"
            ariaLabel="Filter by band"
            value={band}
            onChange={(v) => { setBand(v); setOffset(0); }}
            options={[{ value: '', label: 'All bands' }, ...BANDS.map((b) => ({ value: b, label: b }))]}
          />

          <Select
            id="f-status"
            ariaLabel="Filter by auditor verdict"
            value={status}
            onChange={(v) => { setStatus(v); setOffset(0); }}
            options={[{ value: '', label: 'All verdicts' }, ...STATUSES.map((s) => ({ value: s, label: s }))]}
          />

          <Select
            id="f-sdr-log"
            ariaLabel="Filter by SDR logged status"
            value={sdrLogStatus}
            onChange={(v) => { setSdrLogStatus(v); setOffset(0); }}
            options={[{ value: '', label: 'All SDR logs' }, ...STATUSES.map((s) => ({ value: s, label: s }))]}
          />

          {/* A preset range beats a date picker here: QA is reviewed by recency,
              not by a specific calendar day, and it removes the browser's own
              date widget from the toolbar. */}
          <Select
            id="f-from"
            ariaLabel="Filter by date range"
            value={from}
            onChange={(v) => { setFrom(v); setOffset(0); }}
            options={RANGES}
          />

          <FilterChip active={autoFail} onClick={() => { setAutoFail(!autoFail); setOffset(0); }}>
            Auto-fail
          </FilterChip>
          <FilterChip active={flagged} onClick={() => { setFlagged(!flagged); setOffset(0); }}>
            Flagged
          </FilterChip>
          <FilterChip
            active={mismatch}
            onClick={() => { setMismatch(!mismatch); setOffset(0); }}
            title="Calls where the SDR's logged status disagrees with the evidence"
          >
            Status mismatch
          </FilterChip>

          {(activeFilters > 0 || q) && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearAll}>
              Clear
            </button>
          )}
        </div>

        <ErrorNote error={error} onRetry={reload} />

        {loading && !data ? (
          <Card>
            <div className="stack" style={{ gap: 9 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={34} />
              ))}
            </div>
          </Card>
        ) : !data?.calls.length ? (
          <Card>
            <Empty
              title="No calls match"
              body={
                activeFilters || q
                  ? 'Try widening the filters, or clear them to see everything.'
                  : 'Submit a call with its transcript and it will be audited against the SDR standard.'
              }
              action={
                activeFilters || q ? (
                  <button type="button" className="btn" onClick={clearAll}>
                    Clear filters
                  </button>
                ) : !isSdr ? (
                  <Link to="/calls/new" className="btn btn-primary">
                    Submit a call
                  </Link>
                ) : null
              }
            />
          </Card>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => toggleSort('date')}>
                      Date {arrow('date')}
                    </th>
                    {!isSdr && (
                      <th className="sortable" onClick={() => toggleSort('sdr')}>
                        SDR {arrow('sdr')}
                      </th>
                    )}
                    <th>Prospect</th>
                    <th className="sortable num" onClick={() => toggleSort('score')}>
                      Score {arrow('score')}
                    </th>
                    <th>Band</th>
                    <th>BANT</th>
                    <th>Auditor verdict</th>
                    <th>SDR logged</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {data.calls.map((c) => (
                    <CallRowView
                      key={c.id}
                      call={c}
                      isSdr={isSdr}
                      onOpen={() =>
                        navigate(`/calls/${c.id}`, {
                          state: { from: `/calls${searchParams.toString() ? `?${searchParams.toString()}` : ''}` },
                        })
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {data.total > data.calls.length + offset && (
              <div className="row mt-3" style={{ justifyContent: 'center' }}>
                <button type="button" className="btn" onClick={() => setOffset(offset + 50)}>
                  Load more ({data.total - offset - data.calls.length} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function CallRowView({ call: c, isSdr, onOpen }: { call: CallRow; isSdr: boolean; onOpen: () => void }) {
  const processing = c.processingStatus === 'pending' || c.processingStatus === 'processing';

  return (
    <tr
      className={`clickable ${c.autoFail ? 'is-fail' : ''}`}
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td className="nowrap">
        <div>{formatDate(c.calledAt)}</div>
        <div className="cell-secondary">{formatDuration(c.durationSec)}</div>
      </td>
      {!isSdr && <td className="cell-primary nowrap">{c.sdr.name}</td>}
      <td>
        <div className="cell-primary">{c.prospect.name ?? 'Unnamed prospect'}</div>
        <div className="cell-secondary">{c.prospect.company ?? 'No company recorded'}</div>
      </td>
      <td className="num">
        {processing ? <span className="unavailable">…</span> : c.processingStatus === 'failed' ? <span className="unavailable">—</span> : <Score value={c.overallScore} showMax={false} />}
      </td>
      <td>
        {processing ? (
          <span className="pill">Processing</span>
        ) : c.processingStatus === 'failed' ? (
          <span className="pill pill-fail">Failed</span>
        ) : (
          <BandPill band={c.band} />
        )}
      </td>
      <td>
        <BantStrip factors={c.bant} />
      </td>
      <td className="nowrap">
        {c.verdictStatus ? (
          <span className="row" style={{ gap: 5 }}>
            {c.verdictStatus}
            {c.qualifiedBooking && (
              <span className="pill pill-sm pill-excellent" title="Budget, Authority and Need all passed">
                Qualified
              </span>
            )}
          </span>
        ) : (
          <span className="unavailable">—</span>
        )}
      </td>
      <td className="nowrap">
        {c.sdrLoggedStatus ? (
          <span className="row" style={{ gap: 5 }}>
            {c.sdrLoggedStatus}
            {c.statusMatches === false && (
              <span className="pill pill-sm pill-fail" title="The logged status disagrees with the evidence">
                Mismatch
              </span>
            )}
          </span>
        ) : c.logType ? (
          <span className="unavailable" title="Dial-outcome disposition, not a qualification claim">
            {c.logType}
          </span>
        ) : (
          <span className="unavailable">Not logged</span>
        )}
      </td>
      <td>
        <span className="row row-wrap" style={{ gap: 4 }}>
          {c.autoFail && <span className="pill pill-sm pill-fail">AUTO-FAIL</span>}
          {c.misQualification && <span className="pill pill-sm pill-fail">Mis-qualified</span>}
          {c.flagCount > 0 && (
            <span className={`pill pill-sm ${c.highFlagCount > 0 ? 'pill-fail' : 'pill-warn'}`} title={`${c.flagCount} integrity flag(s)`}>
              {c.flagCount} flag{c.flagCount === 1 ? '' : 's'}
            </span>
          )}
          {!c.autoFail && !c.misQualification && c.flagCount === 0 && !processing && c.processingStatus !== 'failed' && (
            <span className="dim small">None</span>
          )}
        </span>
      </td>
    </tr>
  );
}
